// File: sliders/PollsQuizzesSlider.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// import config from '@/config';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';
import BoardCardTWO from '@/shared-components/forms/add-board-content/shared-components/BoardCardTWO';

import './styles/PollsQuizzesSlider.css';

function Column({ title, items = [], link = '', showQuestionOnCard = false, showOptions = true, }) {
  // Hooks must run unconditionally
  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  const [visualIndex, setVisualIndex] = useState(1); // includes clones
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [lockedItems, setLockedItems] = useState(items);

  // Recompute active slide based on scroll position
  useEffect(() => {
    if (items.length > 0) setLockedItems(items);
  }, [items]);

  const itemCount = lockedItems.length;
  const extendedItems =
    itemCount > 0
      ? [lockedItems[itemCount - 1], ...lockedItems, lockedItems[0]]
      : [];

  const actualIndex =
    itemCount === 0 ? -1 : ((visualIndex - 1 + itemCount) % itemCount);
  const currentTitle =
    actualIndex >= 0 ? lockedItems[actualIndex]?.question ?? title : title;

  useEffect(() => {
    if (itemCount === 0) return;
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = transitionEnabled ? 'transform 0.5s ease' : 'none';
    track.style.transform = `translateX(-${visualIndex * 100}%)`;
  }, [visualIndex, transitionEnabled, itemCount]);

  useEffect(() => {
    if (!transitionEnabled) {
      requestAnimationFrame(() => setTransitionEnabled(true));
    }
  }, [transitionEnabled]);

  const wheelLockedRef = useRef(false);

  const handleTransitionEnd = () => {
    wheelLockedRef.current = false;
    if (itemCount === 0) return;
    if (visualIndex === 0) {
      setTransitionEnabled(false);
      setVisualIndex(itemCount);
    } else if (visualIndex === itemCount + 1) {
      setTransitionEnabled(false);
      setVisualIndex(1);
    }
  };

  const handleNext = () => {
    if (itemCount === 0) return;
    setVisualIndex(prev => prev + 1);
  };

  const handlePrev = () => {
    if (itemCount === 0) return;
    setVisualIndex(prev => prev - 1);
  };

  const handleWheel = e => {
    if (wheelLockedRef.current) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
    if (e.deltaX > 30) {
      wheelLockedRef.current = true;
      handleNext();
    } else if (e.deltaX < -30) {
      wheelLockedRef.current = true;
      handlePrev();
    }
  };

  const touchStartX = useRef(null);
  const handleTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = e => {
    if (touchStartX.current === null) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      touchStartX.current = null;
      if (diff < 0) handleNext();
      else handlePrev();
    }
  };
  const handleTouchEnd = () => {
    touchStartX.current = null;
    wheelLockedRef.current = false;
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    viewport.addEventListener('touchstart', handleTouchStart, { passive: true });
    viewport.addEventListener('touchmove', handleTouchMove, { passive: true });
    viewport.addEventListener('touchend', handleTouchEnd);

    return () => {
      viewport.removeEventListener('wheel', handleWheel);
      viewport.removeEventListener('touchstart', handleTouchStart);
      viewport.removeEventListener('touchmove', handleTouchMove);
      viewport.removeEventListener('touchend', handleTouchEnd);
    };
  }, [itemCount]);

  if (!itemCount) return null;

  return (
    <div className="polls-quizzes-slider__column">
      <div className="polls-quizzes-slider__surface">
        <div className="polls-quizzes-slider__header">
          <span className="polls-quizzes-slider__label">{title}</span>
          <h2 className="polls-quizzes-slider__title">
            {currentTitle}
          </h2>
        </div>

        <div className="polls-quizzes-slider__carousel-wrapper">
          <div className="polls-quizzes-slider__viewport" ref={viewportRef}>
            <div
              className="polls-quizzes-slider__track"
              ref={trackRef}
              onTransitionEnd={handleTransitionEnd}
            >
              {extendedItems.map((item, idx) => {
                const url = `/article/${item._id}`;

                let key = item._id || idx;
                if (idx === 0) key = `${key}-clone-start`;
                else if (idx === extendedItems.length - 1)
                  key = `${key}-clone-end`;

                return (
                  <div key={key} className="polls-quizzes-slider__slider-item">
                    <Link to={url} className="polls-quizzes-slider__item no-underline">
                      <BoardCardTWO item={item} showQuestion={showQuestionOnCard} showOptions={showOptions} />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
          <PrevButton
            className="polls-quizzes-slider__control prev"
            onClick={handlePrev}
          />
          <NextButton
            className="polls-quizzes-slider__control next"
            onClick={handleNext}
          />
        </div>

        {link && (
          <Link to={link} className="polls-quizzes-slider__see-all-link no-underline">
            <ContinueButton className="polls-quizzes-slider__see-all">
              See all
            </ContinueButton>
          </Link>
        )}
      </div>
    </div>
  );
}

export default function PollsQuizzesSlider({
  polls = [],
  quizzes = [],
  pollsTitle = 'Polls',
  quizzesTitle = 'Quizzes',
  pollsLink = '',
  quizzesLink = '',
  showQuestionOnCard = false,
  showOptions = true,
}) {
  return (
    <div className="polls-quizzes-slider element-non-selectable">
      <Column title={pollsTitle} items={polls} link={pollsLink} showQuestionOnCard={showQuestionOnCard} showOptions={showOptions} />
      <Column title={quizzesTitle} items={quizzes} link={quizzesLink} showQuestionOnCard={showQuestionOnCard} showOptions={showOptions} />
    </div>
  );
}
